/* RESPONSIBLE TEAM: team-inbox-experience */
import Service, { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { timeout, type TaskGenerator } from 'ember-concurrency';
import type Session from 'embercom/services/session';
import ENV from 'embercom/config/environment';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import type TeammateStateChange from 'embercom/objects/inbox/renderable/state-changes/teammate-state-change';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { StateChangeType } from 'embercom/models/data/inbox/state-change-enums';
import { post } from 'embercom/lib/ajax';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';

const INITIAL_CHECK_DELAY = ENV.environment === 'test' ? 10 : 15000; // 15 seconds, provides gap for quick teammate context skimming

export default class ConversationResumeService extends Service {
  @service declare session: Session;

  private get hasAhtResumeEventEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('hsg-inbox-aht-conversation-resume-event');
  }

  /**
   * Check conversation resumption once when conversation is opened
   */
  handleResumption(conversation: Conversation): void {
    if (!this.hasAhtResumeEventEnabled) {
      return;
    }

    taskFor(this.checkResumptionWithDelay).perform(conversation);
  }

  @restartableTask
  private *checkResumptionWithDelay(conversation: Conversation): TaskGenerator<void> {
    if (!this.shouldSkipInitialDelay) {
      yield timeout(INITIAL_CHECK_DELAY);
    }

    if (!this.shouldCreateResumeEvent(conversation)) {
      return;
    }

    yield this.createResumeEvent(conversation);
  }

  private get shouldSkipInitialDelay(): boolean {
    return this.session.workspace.isFeatureEnabled('hsg-inbox-aht-skip-resume-delay');
  }

  /**
   * Determines if a resume event should be created for the given conversation
   */
  private shouldCreateResumeEvent(conversation: Conversation): boolean {
    return this.hasValidState(conversation) && this.hasResumableEvent(conversation);
  }

  private hasValidState(conversation: Conversation): boolean {
    if (conversation.state !== ConversationState.Open) {
      return false;
    }

    if (!conversation.adminAssignee || conversation.adminAssignee.id !== this.session.teammate.id) {
      return false;
    }

    return true;
  }

  /**
   * Finds the last resumable event in the conversation
   */
  private getLastResumableEvent(conversation: Conversation): RenderablePart | undefined {
    return [...conversation.committedParts].reverse().find((part) => this.isResumablePart(part));
  }

  /**
   * Determines if the conversation has any resumable event
   * In this case we're looking for the last assignment/closed/snoozed event and
   * checks if there's no resume event after that event
   */
  private hasResumableEvent(conversation: Conversation): boolean {
    let lastResumableEvent = this.getLastResumableEvent(conversation);
    if (!lastResumableEvent) {
      return false;
    }

    return !this.hasResumeEventAfter(conversation, lastResumableEvent.createdAt);
  }

  private async createResumeEvent(conversation: Conversation): Promise<void> {
    try {
      await post(
        `/ember/inbox/conversations/${conversation.id}/resume?app_id=${this.session.workspace.id}`,
        {
          data: {
            teammate_id: this.session.teammate.id,
            conversation_id: conversation.id,
          },
        },
      );
    } catch (error) {
      console.error('Error sending resumption event:', error);
      throw error;
    }
  }

  private isResumablePart(part: RenderablePart): boolean {
    return (
      part.renderableType === RenderableType.Assignment ||
      (part.renderableType === RenderableType.StateChange &&
        ((part.renderableData as TeammateStateChange).change === StateChangeType.Closed ||
          (part.renderableData as TeammateStateChange).change === StateChangeType.Snoozed))
    );
  }

  private hasResumeEventAfter(conversation: Conversation, afterTimestamp: Date): boolean {
    return conversation.committedParts.some((part) => {
      if (part.createdAt <= afterTimestamp) {
        return false;
      }

      return part.renderableType === RenderableType.ConversationResumed;
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'conversation-resume-service': ConversationResumeService;
  }
}
